import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout/DashboardLayout";
import { LayoutDashboard, Menu as MenuIcon, ShoppingBag, CreditCard, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";

const items = (t) => [
  { to: "/restaurant", icon: LayoutDashboard, label: t("dashboard"), end: true, tid: "rest-nav-dashboard" },
  { to: "/restaurant/menu", icon: MenuIcon, label: t("menu"), tid: "rest-nav-menu" },
  { to: "/restaurant/orders", icon: ShoppingBag, label: t("orders"), tid: "rest-nav-orders" },
  { to: "/restaurant/subscription", icon: CreditCard, label: t("subscription"), tid: "rest-nav-subscription" },
];

export default function Subscription() {
  const { user } = useAuth();
  const { t } = useLang();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    api.get("/subscription-plans").then(r => setPlans(r.data.filter(p => p.audience === user.role)));
    api.get("/my/subscription").then(r => setCurrent(r.data));
  }, [user.role]);

  const subscribe = async (planKey) => {
    await api.post("/subscribe", { plan_key: planKey, billing });
    const r = await api.get("/my/subscription");
    setCurrent(r.data);
  };

  return (
    <DashboardLayout items={items(t)} title={t("subscription")}>
      {current && (
        <div className="so-card p-5 mb-6 bg-[var(--so-orange)]/5 border-[var(--so-orange)]/30" data-testid="current-sub">
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--so-orange)]">{t("currentPlan")}</div>
          <div className="font-bold text-xl mt-1 capitalize">{current.plan_key} · {current.billing}</div>
          <div className="text-sm text-[var(--so-text-soft)]">Expires {new Date(current.expires_at).toLocaleDateString()}</div>
        </div>
      )}

      <div className="inline-flex rounded-full p-1 bg-[var(--so-surface)] border border-[var(--so-border)] mb-5">
        {["monthly", "yearly"].map((b) => (
          <button key={b} onClick={() => setBilling(b)} data-testid={`sub-billing-${b}`} className={`px-4 py-1.5 rounded-full text-sm font-bold ${billing === b ? "bg-[var(--so-orange)] text-white" : "text-[var(--so-text-soft)]"}`}>
            {b === "monthly" ? t("monthly") : t("yearly")}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => {
          const active = current?.plan_key === p.key;
          return (
            <div key={p.key} className={`so-card p-5 ${p.highlighted ? "border-[var(--so-orange)] ring-2 ring-[var(--so-orange)]/20" : ""}`} data-testid={`plan-card-${p.key}`}>
              {p.highlighted && <div className="text-xs font-bold uppercase text-[var(--so-orange)] mb-1">Popular</div>}
              <div className="font-bold text-xl">{p.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-3xl font-extrabold">${billing === "yearly" ? p.price_yearly : p.price_monthly}</span>
                <span className="text-xs text-[var(--so-text-soft)]">/{billing === "yearly" ? "yr" : "mo"}</span>
              </div>
              <ul className="mt-4 space-y-1.5 text-sm">
                {p.features.map(f => <li key={f} className="flex gap-1.5"><Check className="w-3.5 h-3.5 text-[var(--so-orange)] mt-1 flex-shrink-0" /><span>{f}</span></li>)}
              </ul>
              <button
                disabled={active}
                onClick={() => subscribe(p.key)}
                data-testid={`subscribe-btn-${p.key}`}
                className={`mt-5 w-full justify-center ${active ? "so-btn-secondary" : "so-btn-primary"}`}
              >
                {active ? "Active" : t("subscribe")}
              </button>
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
